import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
  Typography,
  useTheme,
} from "@mui/material";
import { Component, lazy, Suspense, useEffect, useMemo } from "react";
import { createTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { themeSettings } from "theme";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { WebSocketProvider } from "contexts/WebSocketContext";
import { PresenceProvider } from "contexts/PresenceContext";
import { OVERLAY_NAMES } from "constants/Overlays";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Dashboard from "scenes/workspace/dashboard/Dashboard";
import DashboardNEW from "scenes/workspace/dashboard/Dashboard_NEW";
import Layout from "scenes/layout/Layout";
import Services from "scenes/services/Services";
import ServiceProfile from "scenes/services/ServiceProfile";
import Interpretations from "scenes/services/interpretations/Interpretations";
import Clients from "scenes/connections/clients/Clients";
import Linguists from "scenes/connections/linguists/Linguists";
import ClientNewEdit from "scenes/connections/clients/ClientNewEdit";
import ClientStatsOverlay from "scenes/connections/clients/ClientStatsOverlay";
import ClientProfile from "scenes/connections/clients/ClientProfile";
import LinguistsProfile from "scenes/connections/linguists/LinguistProfile";
import RequestorNewEdit from "scenes/connections/clients/RequestorNewEdit";
import RequestorGroupNewEdit from "scenes/connections/clients/RequestorGroupNewEdit";
import RequestorMergeOverlay from "scenes/connections/clients/RequestorMergeOverlay";
import ContactPersonNewEdit from "scenes/connections/clients/ContactPersonNewEdit";
import ContactPersonMergeOverlay from "scenes/connections/clients/ContactPersonMergeOverlay";
import ClientMergeOverlay from "scenes/connections/clients/ClientMergeOverlay";
import LinguistNewEdit from "scenes/connections/linguists/LinguistNewEdit";
import LinguistStatsOverlay from "scenes/connections/linguists/LinguistStatsOverlay";
import LinguistMergeOverlay from "scenes/connections/linguists/LinguistMergeOverlay";
import LinguistAvailabilityEdit from "scenes/connections/linguists/LinguistAvailabilityEdit";
import LinguistRateUpdate from "scenes/connections/linguists/LinguistRateUpdate";
import LinguistDocumentGeneration from "scenes/connections/linguists/LinguistDocumentGeneration";
import LinguistsGeneratedDocuments from "scenes/connections/linguists/LinguistsGeneratedDocuments";
import InterpretationProfile from "scenes/services/interpretations/InterpretationProfile";
import ServiceNoteNewEdit from "scenes/services/ServiceNoteNewEdit";
import ClientNoteNewEdit from "scenes/connections/clients/ClientNoteNewEdit";
import LinguistNoteNewEdit from "scenes/connections/linguists/LinguistNoteNewEdit";
import TaskNewEdit from "scenes/workspace/tasks/TaskNewEdit";
import TaskProfile from "scenes/workspace/tasks/TaskProfile";
import TasksAutomated from "scenes/workspace/tasks/TasksAutomated";
import InterpretationNew from "scenes/services/interpretations/InterpretationNewEdit";
import InterpretationPreAssign from "scenes/services/interpretations/InterpretationPreAssign";
import InterpretationOutreach from "scenes/services/interpretations/InterpretationOutreach";
import InterpretationSendDetails from "scenes/services/interpretations/InterpretationSendDetails";
import InterpretationReminders from "scenes/services/interpretations/InterpretationReminders";
import Translations from "scenes/services/translations/Translations";
import TranslationProfile from "scenes/services/translations/TranslationProfile";
import TranslationNewEdit from "scenes/services/translations/TranslationNewEdit";
import TranslationDocuments from "scenes/services/translations/TranslationDocuments";
import OtherServiceNewEdit from "scenes/services/other/OtherServiceNewEdit";
import OtherServices from "scenes/services/other/OtherServices";
import OtherServiceProfile from "scenes/services/other/OtherServiceProfile";
import Login from "scenes/login/Login";
import LoginConfirmation from "scenes/login/LoginConfirmation";
import LinguistLogin from "scenes/linguistPortal/LinguistLogin";
import LinguistDashboard from "scenes/linguistPortal/LinguistDashboard";
import LinguistAssignmentDetail from "scenes/linguistPortal/LinguistAssignmentDetail";
import LinguistProfile from "scenes/linguistPortal/LinguistProfile";
import LinguistNotifications from "scenes/linguistPortal/LinguistNotifications";
import LinguistInvitationSetup from "scenes/linguistPortal/LinguistInvitationSetup";
import LinguistPortalLayout from "scenes/linguistPortal/LinguistPortalLayout";
import LinguistInterpretations from "scenes/linguistPortal/LinguistInterpretations";
import LinguistProfileEditOverlay from "scenes/linguistPortal/LinguistProfileEditOverlay";
import LinguistNotificationSettingsEditOverlay from "scenes/linguistPortal/LinguistNotificationSettingsEditOverlay";
import Expenses from "scenes/management/expenses/Expenses";
import Invoices from "scenes/management/invoices/Invoices";
import LinguistsPayments from "scenes/management/linguistsPayments/LinguistsPayments";
import InvoiceExport from "scenes/management/invoices/InvoiceExport";
import Members from "scenes/settings/members/Members";
import MemberNewEdit from "scenes/settings/members/MemberNewEdit";
import MemberProfile from "scenes/settings/members/MemberProfile";
import Settings from "scenes/settings/Settings";
import SettingsProfileEdit from "scenes/settings/SettingsProfileEdit";
import LanguageNewEdit from "scenes/settings/LanguageNewEdit";
import DialectNewEdit from "scenes/settings/DialectNewEdit";
import CommunicationTemplateNewEdit from "scenes/settings/CommunicationTemplateNewEdit";
import MileageRateNewEdit from "scenes/settings/MileageRateNewEdit";
import UnitNewEdit from "scenes/settings/UnitNewEdit";
import HolidayNewEdit from "scenes/settings/HolidayNewEdit";
import LinguistRequirementNewEdit from "scenes/settings/LinguistRequirementNewEdit";
import ServiceDocumentNewEdit from "scenes/settings/ServiceDocumentNewEdit";
import DocumentCategoriesManager from "scenes/settings/DocumentCategoriesManager";
import PaymentMethodNewEdit from "scenes/settings/PaymentMethodNewEdit";
import ServiceTypeNewEdit from "scenes/settings/ServiceTypeNewEdit";
import ServiceCostTypeNewEdit from "scenes/settings/ServiceCostTypeNewEdit";
import ServiceCostTypeRulesEdit from "scenes/settings/ServiceCostTypeRulesEdit";
import NotificationNewEdit from "scenes/settings/NotificationNewEdit";
import TimesheetsManualNewEdit from "scenes/settings/TimesheetsManualNew";
import TimesheetsImport from "scenes/settings/TimesheetsImport";
import ExpensesCategoryNewEdit from "scenes/settings/ExpensesCategoryNewEdit";
import ExpensesSubcategoryNewEdit from "scenes/settings/ExpensesSubcategoryNewEdit";
import ExpenseNewEdit from "scenes/management/expenses/ExpenseNewEdit";
import ExpenseProfile from "scenes/management/expenses/ExpenseProfile";
import Reports from "scenes/management/reports/Reports";
import ChangeCategory from "scenes/services/ChangeCategory";
import Help from "scenes/help/Help";
import InterpretationOPIUpload from "scenes/services/interpretations/InterpretationOPIUpload";
import RateNewEdit from "scenes/connections/clients/RateNewEdit";
import InvoiceProfile from "scenes/management/invoices/InvoiceProfile";
import InvoiceNew from "scenes/management/invoices/InvoiceNew";
import InvoiceEdit from "scenes/management/invoices/InvoiceEdit";
import Server from "scenes/server/Server";
import CalendarSyncOverlay from "scenes/workspace/calendar/CalendarSyncOverlay";
import Calendar from "scenes/workspace/calendar/Calendar";
import Tasks from "scenes/workspace/tasks/Tasks";
import Notifications from "scenes/notifications/Notifications";
import GlobalSearch from "scenes/search/GlobalSearch";
import NotFound from "scenes/notFound/NotFound";
import ProtectedRoute from "components/ProtectedRoute";
import LinguistProtectedRoute from "components/LinguistProtectedRoute";
import NetworkErrorListener from "components/NetworkErrorListener";
import AmerCard from "scenes/public/digitalBusinessCards/AmerCard";
import OperationsCard from "scenes/public/digitalBusinessCards/OperationsCard";
import CardsRedirect from "components/CardsRedirect";
import PublicVideoPage, {
  PublicVideoHostNotFound,
} from "scenes/public/videos/PublicVideoPage";
import VideoLibrary from "scenes/management/videoLibrary/VideoLibrary";
import VideoProfile from "scenes/management/videoLibrary/VideoProfile";
import VideoNewEdit from "scenes/management/videoLibrary/VideoNewEdit";
import { isCardsSubdomain, isVideosSubdomain } from "utils/subdomainDetector";
import LinguistAssignmentFinalize from "scenes/linguistPortal/LinguistAssignmentFinalize";
import OverlayShowcase from "scenes/showcase/OverlayShowcase";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/New_York");

// Deploy-safe lazy(): a stale tab can request a chunk hash that a deploy has
// already replaced (404), and transient network failures reject the fetch
// too. Retry once in place, then reload once per chunk name per session so a
// fresh index.html brings back matching chunk hashes. A successful load
// clears the guard so the next deploy can self-heal again.
const lazyWithRetry = (name, importFn) => {
  const reloadKey = `ce-chunk-reload:${name}`;

  const loadOnce = async () => {
    const module = await importFn();
    try {
      window.sessionStorage.removeItem(reloadKey);
    } catch (_error) {
      // Storage unavailable: nothing to clear.
    }
    return module;
  };

  const LazyComponent = lazy(async () => {
    try {
      return await loadOnce();
    } catch (_firstError) {
      try {
        return await loadOnce();
      } catch (secondError) {
        // Both attempts failed. Reload once per chunk per session; if the
        // guard is already set the reload did not help, so rethrow and let
        // ChunkErrorBoundary show its Reload card instead of a blank app.
        let alreadyGuarded = false;
        try {
          alreadyGuarded = Boolean(window.sessionStorage.getItem(reloadKey));
          if (!alreadyGuarded) window.sessionStorage.setItem(reloadKey, "1");
        } catch (_storageError) {
          // Cannot persist a guard -> never reload (avoids reload loops).
          alreadyGuarded = true;
        }

        if (alreadyGuarded) throw secondError;

        window.location.reload();
        // Never settle: the page is reloading underneath this promise.
        return new Promise(() => {});
      }
    }
  });

  return LazyComponent;
};

// Heavy overlays lazy-loaded so pdfmake (~1 MB gz) and pdf-lib (~200 KB gz)
// stay out of the main bundle (bundle-size phase 1, 2026-10-14). The prefetch
// effect in App() warms these exact chunks when the device is idle.
const AIInsightsViewOverlay = lazyWithRetry("ai-insights", () =>
  import("scenes/management/reports/AIInsightsViewOverlay"),
);
const DocumentTemplateNewEdit = lazyWithRetry("document-template", () =>
  import("scenes/settings/DocumentTemplateNewEdit"),
);



// Loading chrome shown while a lazy overlay chunk downloads. Mirrors the
// quickCreateLoading Backdrop already used in App (dimmed backdrop + centered
// progress card) so chunk fetches never look like a frozen screen.
const OverlayChunkFallback = ({ label }) => {
  const theme = useTheme();

  return (
    <Backdrop
      open
      sx={{
        zIndex: (muiTheme) => muiTheme.zIndex.modal + 2,
        color: theme.palette.primary.main,
        backgroundColor: "rgba(0, 0, 0, 0.35)",
      }}
    >
      <Box
        sx={{
          minWidth: 280,
          px: 3,
          py: 2.5,
          borderRadius: "14px",
          backgroundColor: theme.palette.background.default,
          boxShadow: theme.shadows[8],
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body1" fontWeight={700}>
          {label}
        </Typography>
      </Box>
    </Backdrop>
  );
};

// Fallback card for a lazy overlay chunk that failed even after retry + one
// reload. Mirrors OverlayChunkFallback's backdrop/card look (same shell,
// spinner slot swapped for a recovery action) so a failure reads as part of
// the app instead of a blank screen.
const ChunkErrorCard = () => {
  const theme = useTheme();

  return (
    <Backdrop
      open
      sx={{
        zIndex: (muiTheme) => muiTheme.zIndex.modal + 2,
        color: theme.palette.primary.main,
        backgroundColor: "rgba(0, 0, 0, 0.35)",
      }}
    >
      <Box
        sx={{
          minWidth: 280,
          px: 3,
          py: 2.5,
          borderRadius: "14px",
          backgroundColor: theme.palette.background.default,
          boxShadow: theme.shadows[8],
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <Typography variant="body1" fontWeight={700}>
          Couldn&apos;t load this section.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Box>
    </Backdrop>
  );
};


// Scoped error boundary for the two lazy overlay sites only. Without it, a
// rejected chunk import propagates to the root and unmounts the whole app.
class ChunkErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Chunk load failed:", error);
  }

  render() {
    if (this.state.hasError) return <ChunkErrorCard />;
    return this.props.children;
  }
}

function App() {
  const mode = useSelector((state) => state.global.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  const { overlayName, overlayData } = useStateContext();
  const { currentUser } = useAuth();
  
  // Detect if we're on the cards subdomain
  const isCards = isCardsSubdomain();
  const isVideos = isVideosSubdomain();
  const isProduction = process.env.NODE_ENV === "production";

  // Idle prefetch of the two heavy overlay chunks (pdfmake / pdf-lib).
  // Skipped on the cards subdomain and for save-data / slow (2g) connections;
  // fire-and-forget so it never blocks or surfaces errors.
  useEffect(() => {
    if (isCards || isVideos) return;
    const connection = navigator.connection;
    if (connection?.saveData) return;
    if (connection?.effectiveType?.includes("2g")) return;

    const prefetch = () => {
      // Same specifiers as the lazy() calls above -> same webpack chunks.
      try {
        import("scenes/management/reports/AIInsightsViewOverlay").catch(
          () => {},
        );
        import("scenes/settings/DocumentTemplateNewEdit").catch(() => {});
      } catch (_error) {
        // Fire-and-forget: a failed prefetch must never surface or block.
      }
    };

    let idleHandle = null;
    let timeoutHandle = null;
    if (typeof window.requestIdleCallback === "function") {
      idleHandle = window.requestIdleCallback(prefetch, { timeout: 4000 });
    } else {
      timeoutHandle = window.setTimeout(prefetch, 2500);
    }

    return () => {
      if (idleHandle !== null) window.cancelIdleCallback?.(idleHandle);
      if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
    };
  }, [isCards, isVideos]);

  const renderOverlay = () => {
    switch (overlayName) {
      case OVERLAY_NAMES.CLIENT_NEW_EDIT:
        return <ClientNewEdit />;
      case OVERLAY_NAMES.CLIENT_STATS:
        return <ClientStatsOverlay />;
      case OVERLAY_NAMES.REQUESTOR_NEW_EDIT:
        return <RequestorNewEdit />;
      case OVERLAY_NAMES.REQUESTOR_GROUP_NEW_EDIT:
        return <RequestorGroupNewEdit />;
      case OVERLAY_NAMES.CONTACT_PERSON_NEW_EDIT:
        return <ContactPersonNewEdit />;
      case OVERLAY_NAMES.CONTACT_PERSON_MERGE:
        return <ContactPersonMergeOverlay />;
      case OVERLAY_NAMES.REQUESTOR_MERGE:
        return <RequestorMergeOverlay />;
      case OVERLAY_NAMES.CLIENT_MERGE:
        return <ClientMergeOverlay />;
      case OVERLAY_NAMES.LINGUIST_NEW_EDIT:
        return <LinguistNewEdit />;
      case OVERLAY_NAMES.LINGUIST_STATS:
        return <LinguistStatsOverlay />;
      case OVERLAY_NAMES.LINGUIST_MERGE:
        return <LinguistMergeOverlay />;
      case OVERLAY_NAMES.LINGUIST_AVAILABILITY_EDIT:
        return <LinguistAvailabilityEdit />;
      case OVERLAY_NAMES.LINGUIST_RATE_UPDATE:
        return <LinguistRateUpdate />;
      case OVERLAY_NAMES.LINGUIST_DOCUMENT_GENERATION:
        return <LinguistDocumentGeneration />;
      case OVERLAY_NAMES.LINGUISTS_GENERATED_DOCUMENTS:
        return <LinguistsGeneratedDocuments />;
      case OVERLAY_NAMES.LINGUIST_PORTAL_PROFILE_EDIT:
        return <LinguistProfileEditOverlay />;
      case OVERLAY_NAMES.LINGUIST_PORTAL_NOTIFICATION_SETTINGS_EDIT:
        return <LinguistNotificationSettingsEditOverlay />;
      case OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT:
        return <ServiceNoteNewEdit />;
      case OVERLAY_NAMES.CLIENT_NOTE_NEW_EDIT:
        return <ClientNoteNewEdit />;
      case OVERLAY_NAMES.LINGUIST_NOTE_NEW_EDIT:
        return <LinguistNoteNewEdit />;
      case OVERLAY_NAMES.INTERPRETATION_NEW_EDIT:
        return <InterpretationNew />;
      case OVERLAY_NAMES.INTERPRETATION_PRE_ASSIGN:
        return <InterpretationPreAssign />;
      case OVERLAY_NAMES.INTERPRETATION_OUTREACH:
        return <InterpretationOutreach />;
      case OVERLAY_NAMES.INTERPRETATION_OPI_UPLOAD:
        return <InterpretationOPIUpload />;
      case OVERLAY_NAMES.INTERPRETATION_SEND_DETAILS:
        return <InterpretationSendDetails />;
      case OVERLAY_NAMES.INTERPRETATION_REMINDERS:
        return <InterpretationReminders />;
      case OVERLAY_NAMES.TRANSLATION_NEW_EDIT:
        return <TranslationNewEdit />;
      case OVERLAY_NAMES.TRANSLATION_DOCUMENTS:
        return <TranslationDocuments />;
      case OVERLAY_NAMES.OTHERSERVICE_NEW_EDIT:
        return <OtherServiceNewEdit />;
      case OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY:
        return <ChangeCategory />;
      case OVERLAY_NAMES.RATE_NEW_EDIT:
        return <RateNewEdit />;
      case OVERLAY_NAMES.INVOICES_NEW_EDIT:
        return <InvoiceNew />;
      case OVERLAY_NAMES.INVOICES_ACTION_EDIT:
        return <InvoiceEdit />;
      case OVERLAY_NAMES.INVOICES_EXPORT:
        return <InvoiceExport />;
      case OVERLAY_NAMES.CALENDAR_SYNC_VERIFY:
        return <CalendarSyncOverlay />;
      case OVERLAY_NAMES.TASK_NEW_EDIT:
        return <TaskNewEdit />;
      case OVERLAY_NAMES.TASK_PROFILE:
        return <TaskProfile />;
      case OVERLAY_NAMES.TASKS_AUTOMATED:
        return <TasksAutomated />;
      case OVERLAY_NAMES.MEMBER_NEW_EDIT:
        return <MemberNewEdit />;
      case OVERLAY_NAMES.SETTINGS_PROFILE_EDIT:
        return <SettingsProfileEdit />;
      case OVERLAY_NAMES.SETTINGS_LANGUAGE_NEW_EDIT:
        return <LanguageNewEdit />;
      case OVERLAY_NAMES.SETTINGS_DIALECT_NEW_EDIT:
        return <DialectNewEdit />;
      case OVERLAY_NAMES.SETTINGS_EMAIL_TEMPLATE_NEW_EDIT:
        return <CommunicationTemplateNewEdit />;
      case OVERLAY_NAMES.SETTINGS_MILEAGE_RATE_NEW_EDIT:
        return <MileageRateNewEdit />;
      case OVERLAY_NAMES.SETTINGS_UNIT_NEW_EDIT:
        return <UnitNewEdit />;
      case OVERLAY_NAMES.SETTINGS_HOLIDAY_NEW_EDIT:
        return <HolidayNewEdit />;
      case OVERLAY_NAMES.SETTINGS_LINGUIST_REQUIREMENT_NEW_EDIT:
        return <LinguistRequirementNewEdit />;
      case OVERLAY_NAMES.SETTINGS_DOCUMENT_TEMPLATE_NEW_EDIT:
        return (
          <ChunkErrorBoundary>
            <Suspense
              fallback={
                <OverlayChunkFallback label="Loading document template..." />
              }
            >
              <DocumentTemplateNewEdit />
            </Suspense>
          </ChunkErrorBoundary>
        );
      case OVERLAY_NAMES.SETTINGS_SERVICE_DOCUMENT_NEW_EDIT:
        return <ServiceDocumentNewEdit />;
      case OVERLAY_NAMES.SETTINGS_DOCUMENT_CATEGORIES_MANAGER:
        return <DocumentCategoriesManager />;
      case OVERLAY_NAMES.SETTINGS_PAYMENT_METHOD_NEW_EDIT:
        return <PaymentMethodNewEdit />;
      case OVERLAY_NAMES.SETTINGS_SERVICE_TYPE_NEW_EDIT:
        return <ServiceTypeNewEdit />;
      case OVERLAY_NAMES.SETTINGS_SERVICE_COST_TYPE_NEW_EDIT:
        return <ServiceCostTypeNewEdit />;
      case OVERLAY_NAMES.SETTINGS_SERVICE_COST_TYPE_RULES_EDIT:
        return <ServiceCostTypeRulesEdit />;
      case OVERLAY_NAMES.SETTINGS_NOTIFICATION_NEW_EDIT:
        return <NotificationNewEdit />;
      case OVERLAY_NAMES.SETTINGS_TIMESHEETS_MANUAL_NEW:
        return <TimesheetsManualNewEdit />;
      case OVERLAY_NAMES.SETTINGS_TIMESHEETS_IMPORT:
        return <TimesheetsImport />;
      case OVERLAY_NAMES.SETTINGS_EXPENSES_CATEGORY_NEW_EDIT:
        return <ExpensesCategoryNewEdit />;
      case OVERLAY_NAMES.SETTINGS_EXPENSES_SUBCATEGORY_NEW_EDIT:
        return <ExpensesSubcategoryNewEdit />;
      case OVERLAY_NAMES.MANAGEMENT_EXPENSE_NEW_EDIT:
        return <ExpenseNewEdit />;
      case OVERLAY_NAMES.MANAGEMENT_VIDEO_NEW_EDIT:
        return <VideoNewEdit />;
      case OVERLAY_NAMES.EXPENSE_PROFILE:
        return <ExpenseProfile />;
      case OVERLAY_NAMES.GLOBAL_SEARCH:
        return <GlobalSearch />;
      case OVERLAY_NAMES.REPORTS_INSIGHTS:
        return (
          <ChunkErrorBoundary>
            <Suspense
              fallback={<OverlayChunkFallback label="Loading insights..." />}
            >
              <AIInsightsViewOverlay />
            </Suspense>
          </ChunkErrorBoundary>
        );

      case OVERLAY_NAMES.LINGUIST_ASSIGNMENT_FINALIZE:
        return <LinguistAssignmentFinalize />;

      case OVERLAY_NAMES.OVERLAY_SHOWCASE:
        return <OverlayShowcase />;

      default:
        return null;
    }
  };

  return (
    <WebSocketProvider user={currentUser}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <NetworkErrorListener />
            <div className="app">
              <Router>
                <PresenceProvider>
                
                <Routes>
                {/* Public card routes are available outside the video host. */}
                {!isVideos && (
                  <>
                    <Route path="/amer" element={<AmerCard />} />
                    <Route path="/operations" element={<OperationsCard />} />
                    {/*
                      Portal video page — DEVELOPMENT ONLY. The public video host
                      is not reachable on localhost, so this stands in for it.

                      In production the admin host must never render a video: a
                      public visitor could otherwise swap the host of a shared
                      videos.… link for admin.… and watch it (this route falls back
                      to the public API when signed out), which also exposes the
                      login page to them.
                    */}
                    {!isProduction && (
                      <Route
                        path="/videos/:publicId"
                        element={<PublicVideoPage source="portal" />}
                      />
                    )}
                  </>
                )}

                {/* Conditional routing based on subdomain */}
                {isCards ? (
                  // Cards subdomain - only show card routes
                  <>
                    <Route path="/" element={<CardsRedirect />} />
                    <Route path="*" element={<Navigate to="/operations" replace />} />
                  </>
                ) : isVideos ? (
                  // Public video host - individual video pages only, no portal layout.
                  // Signing in is offered here because an Internal Only link lands on
                  // this host and needs a session on this origin.
                  <>
                    <Route path="/login" element={<Login />} />
                    <Route path="/v/:publicId" element={<PublicVideoPage />} />
                    <Route path="*" element={<PublicVideoHostNotFound />} />
                  </>
                ) : (
                  // Admin subdomain - show full portal
                  <>
                    {/* Authentication Routes */}
                    <Route path="/" element={<Login />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/loginConfirmation" element={<LoginConfirmation />} />
                    <Route path="/__/auth/action" element={<LoginConfirmation />} />
                    <Route path="/linguist-login" element={<LinguistLogin />} />
                    <Route
                      path="/linguist-portal"
                      element={(
                        <LinguistProtectedRoute>
                          <LinguistPortalLayout />
                        </LinguistProtectedRoute>
                      )}
                    >
                      <Route index element={<LinguistDashboard />} />
                      <Route path="interpretations" element={<LinguistInterpretations />} />
                      <Route path="profile" element={<LinguistProfile />} />
                      <Route path="notifications" element={<LinguistNotifications />} />
                      <Route path="assignments/:id" element={<LinguistAssignmentDetail />} />
                    </Route>
                    <Route path="/linguist-portal/setup-password" element={<LinguistInvitationSetup />} />

                <Route element={<Layout />}>
                  {/* 2026-10-30 — Dashboard_NEW is now THE Dashboard. The old
                      period dashboard is retired to /dashboard-legacy: still
                      reachable (bookmarks, muscle memory) while the last
                      figures move into Reports and the lists, but no longer
                      linked from the sidebar. */}
                  <Route path="/dashboard" element={<DashboardNEW />} />
                  <Route path="/dashboard-new" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard-legacy" element={<Dashboard />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:id" element={<ServiceProfile />} />
                  <Route path="/interpretations" element={<Interpretations />} />
                  <Route path="/interpretations/:id" element={<InterpretationProfile />} />
                  <Route path="/translations" element={<Translations />} />
                  <Route path="/translations/:id" element={<TranslationProfile />} />
                  <Route path="/otherServices" element={<OtherServices />} />
                  <Route path="/otherServices/:id" element={<OtherServiceProfile />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/linguists" element={<Linguists />} />
                  <Route path="/linguists/:id" element={<LinguistsProfile />} />
                  <Route path="/clients/:id" element={<ClientProfile />} />
                  <Route
                    path="/invoices/:id"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <InvoiceProfile />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/help" element={<Help />} />
                  <Route
                    path="/expenses"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Expenses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoices"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Invoices />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/linguistsPayments"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <LinguistsPayments />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Reports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/video-library"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <VideoLibrary />
                      </ProtectedRoute>
                    }
                  />
                        <Route
                          path="/video-library/:videoId"
                          element={
                            <ProtectedRoute allowedRoles={["Admin"]}>
                              <VideoProfile />
                            </ProtectedRoute>
                          }
                        />
                  <Route
                    path="/members"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Members />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/members/:id"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <MemberProfile />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/settings" element={<Settings />} />
                  <Route
                    path="/server"
                    element={
                      <ProtectedRoute allowedRoles={["Admin"]}>
                        <Server />
                      </ProtectedRoute>
                    }
                  />
                  {/* Legacy path — the Logs screen now lives inside Server. */}
                  <Route path="/logs" element={<Navigate to="/server" replace />} />
                </Route>

                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
                  </>
                )}
              </Routes>
              {!isCards && !isVideos && renderOverlay()}
              {!isCards && !isVideos && overlayData?.quickCreateLoading && (
                <Backdrop
                  open
                  sx={{
                    zIndex: (muiTheme) => muiTheme.zIndex.modal + 2,
                    color: theme.palette.primary.main,
                    backgroundColor: "rgba(0, 0, 0, 0.35)",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 280,
                      px: 3,
                      py: 2.5,
                      borderRadius: "14px",
                      backgroundColor: theme.palette.background.default,
                      boxShadow: theme.shadows[8],
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.25,
                    }}
                  >
                    <CircularProgress size={28} />
                    <Typography variant="body1" fontWeight={700}>
                      {overlayData?.quickCreateLoadingLabel || "Loading form data..."}
                    </Typography>
                  </Box>
                </Backdrop>
              )}
              </PresenceProvider>
            </Router>
          </div>
        </ThemeProvider>
      </LocalizationProvider>
    </WebSocketProvider>
  );
}

export default App;
